import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { Link as RLink } from "react-router-dom";
import { spotlight } from "@mantine/spotlight";
import { useMemo, MouseEvent } from "react";
import { Tooltip, Whisper } from "rsuite";
import { Button } from "reactstrap";
import classNames from "classnames";

import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import { sidebarOpenAtom } from "../../atoms/SidebarAtoms";
import { IsMobileAtom } from "../../App";

export interface Link {
  id: string;
  type: string;
  path?: string;
  icon: string;
  css?: string;
  name: string;
  color?: string;
  pageType?: string;
  action?: { icon: string };
  keyCommand?: string;
  activeID?: string;
}

interface Props {
  open: boolean;
  link: Link;
}

const IconLink = ({ open, link }: Props) => {
  const navigate = useNavigate();
  const { tenantID } = useParams();
  const location = useLocation();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const isCloudSearch = link.path === "CloudSearch";
  const mobileScreen = useAtomValue(IsMobileAtom);
  const setSidebarOpen = useSetAtom(sidebarOpenAtom);

  const active = useMemo(() => {
    if (link.activeID && location.pathname.includes(link.activeID)) {
      return true;
    }

    if (link.path === "WorkspaceHomeRoute") {
      return location.pathname.startsWith(
        `/clouds/${tenantID}/workspaces/${workspaceID}/home/`
      );
    }

    return location.pathname === link.path;
  }, [tenantID, location.pathname, link, workspaceID]);

  const openLink = (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    e.preventDefault();

    if (!link.path) return;

    if (link.path === "WorkspaceHomeRoute") {
      if (!workspaceID) return;

      if (mobileScreen) {
        setSidebarOpen(false);
      }

      return navigate(
        `/clouds/${tenantID}/workspaces/${workspaceID}/home/pages`
      );
    }

    if (link.path === "CloudSearch") {
      return spotlight.open();
    }

    navigate(link.path);
  };

  const homeLink =
    link.path === "WorkspaceHomeRoute"
      ? `/clouds/${tenantID}/workspaces/${workspaceID}/home/pages`
      : link?.path ?? "";

  const Icon = () => <i className={link.icon} style={{ fontSize: 18 }} />;

  return open ? (
    <RLink
      className={classNames(
        "ignore-focus d-flex align-items-center cursor-pointer px-2 py-1 mx-2 sidebar-link link-hovered",
        { "sidebar-link-active": active },
        { "d-xs-block d-md-none": isCloudSearch }
      )}
      onClick={openLink}
      to={homeLink}
    >
      <Icon />
      <div className={`pl-2 mb-0 ${link?.color ?? ""}`}>{link.name}</div>
      <div></div>
      {link?.action && (
        <div className={`pl-2 mb-0 ml-auto text-right`}>
          <Button
            size="sm"
            outline
            color="secondary"
            style={{
              width: "1.5rem",
              height: "1.5rem",
            }}
            className="btn-icon-only"
          >
            <i className={link?.action?.icon} style={{ fontSize: 20 }}></i>
          </Button>
        </div>
      )}
      {link?.keyCommand && (
        <div className={`pl-2 mb-0 ml-auto text-right`}>
          <i
            className="mdi mdi-apple-keyboard-command pr-1 text-muted"
            style={{ fontSize: 14 }}
          ></i>
          <span style={{ fontSize: 14 }} className="text-muted">
            {link?.keyCommand}
          </span>
        </div>
      )}
    </RLink>
  ) : (
    <div className={classNames({ "d-xs-block d-md-none": isCloudSearch })}>
      <Whisper
        placement="right"
        controlId={`tooltipHover${link?.id}`}
        trigger="hover"
        speaker={<Tooltip>{link.name}</Tooltip>}
      >
        <div
          className={`text-center cursor-pointer sidebar-link  px-2 py-1 mx-2 link-hovered ${
            active ? "sidebar-link-active" : ""
          }`}
          onClick={openLink}
        >
          <Icon />
        </div>
      </Whisper>
    </div>
  );
};

export default IconLink;
